import {hideLoading, setIsRestoringSoftware, showLoading, showSnackbar} from 'stores/actions/appStateAction';
import {
  getDownloadBackupData,
  postUploadFinalizeRestoreData,
  postUploadRestoreData,
} from 'api/rest/backupAndRestoreApi';
import {createDownloadFile} from 'lib/explorerHelper';
import {BackupAndRestoreState, Feedback, FileType, ModalType, RestoreResult} from 'constants/dataEnum';
import {appendTypedModalData, hideTypedModal, showModalType} from 'stores/actions/typedModalAction';
import {batch} from 'react-redux';
import {getFileName, validateFileData} from 'lib/fileHelper';
import {
  getBackupRestoreStatus,
  prepareBackupData,
  prepareRestoreData,
  resetBackupRestoreStatus,
} from 'api/websocket/backupAndRestoreApi';
import {camelCase} from 'lodash';
import {throwErrorWithData} from 'lib/errorHelper';
import {resolveIntl, setErrorCodeNumber} from './commonBusiness';
import {clearInstrumentProcess} from './instrumentStatusBusiness';

const {formatMessage} = resolveIntl();

export const resetBackupRestoreState = () => (dispatch) => {
  try {
    resetBackupRestoreStatus();
    dispatch(clearInstrumentProcess());
  } catch (error) {
    console.error(error?.message ?? error);
  }
};

export const downloadBackupFile = () => async (dispatch, getState) => {
  const {sessionState} = getState();
  const {user} = sessionState;

  batch(() => {
    dispatch(showLoading(formatMessage({id: 'loading.downloadBackupData.inProgress'})));
    dispatch(hideTypedModal(true));
  });

  try {
    await prepareBackupData(user.userId);

    const backupFileResponse = await getDownloadBackupData();
    const {errorCode: downloadBackupErrorCode, isValid} = validateFileData(backupFileResponse?.data);

    if (isValid) {
      const filename = getFileName(backupFileResponse);

      createDownloadFile(backupFileResponse.data, filename, FileType.ZIP.ext);
      batch(() => {
        dispatch(showSnackbar(Feedback.SUCCESS, formatMessage({id: 'snackbar.downloadBackupData.success'})));
        dispatch(resetBackupRestoreState());
      });
    } else {
      throwErrorWithData('Error in validating downloaded backup data', {errorCode: downloadBackupErrorCode});
    }
  } catch (error) {
    const {errorCode} = error?.data ?? {};
    const errorNumber = await dispatch(setErrorCodeNumber(errorCode, true));

    console.error(error?.message ?? error);
    dispatch(showModalType(ModalType.DOWNLOAD_BACKUP_DATA_FAILED, {errorNumber}));
  } finally {
    dispatch(hideLoading());
  }
};

export const uploadRestoreData = (file) => async (dispatch) => {
  const fileSize = file.size;
  const fileName = file.name;
  const chunkSize = 10000000;
  let start = 0;
  let end = chunkSize;
  const chunks = [];

  batch(() => {
    dispatch(hideTypedModal(false));
    dispatch(showLoading(formatMessage({id: 'loading.uploadRestoreData.inProgress'})));
  });

  while (start < fileSize) {
    const chunk = file.slice(start, end);
    chunks.push(chunk);

    start = end;
    end = start + chunkSize;
  }

  try {
    for (let i = 0, ii = chunks.length; i < ii; i += 1) {
      const e = chunks[i];
      const formData = new FormData();
      formData.append('files', e, i);
      formData.append('index', i);

      // eslint-disable-next-line no-await-in-loop
      await postUploadRestoreData(formData);
    }

    dispatch(appendTypedModalData({errorCode: '', versionsErrorDetail: {}}));
    await postUploadFinalizeRestoreData(fileName);
  } catch (error) {
    const {errorCode, compatibilityRes: versionsErrorDetail} = error?.data ?? {};

    console.error(error?.message ?? error);
    dispatch(appendTypedModalData({errorCode: camelCase(errorCode), versionsErrorDetail}));
  } finally {
    batch(() => {
      dispatch(hideLoading());
      dispatch(showModalType(ModalType.BACKUP_AND_RESTORE_DATA, {}, true));
    });
  }
};

export const restoreData = (isRestoringConfig) => async (dispatch, getState) => {
  const {sessionState} = getState();
  const {user} = sessionState;

  batch(() => {
    dispatch(hideTypedModal(true));
    dispatch(showLoading(formatMessage({id: 'loading.restoreData.inProgress'})));
    dispatch(setIsRestoringSoftware(true));
  });

  try {
    await prepareRestoreData(user.userId, isRestoringConfig);
  } catch (error) {
    console.error(error?.message ?? error);
    batch(() => {
      dispatch(hideLoading());
      dispatch(showSnackbar(Feedback.ERROR, formatMessage({id: 'snackbar.restoreData.failed'})));
      dispatch(setIsRestoringSoftware(false));
    });
  }
};

export const backupRestoreProcessExists = (state) => {
  switch (state) {
    case BackupAndRestoreState.PREPARING_BACKUP:
    case BackupAndRestoreState.PREPARING_RESTORE:
    case BackupAndRestoreState.PREPARED_RESTORE:
    case BackupAndRestoreState.FINALIZING_RESTORE:
      return true;
    default:
      return false;
  }
};

export const handleRestoreResult = (lastRestoreResult) => (dispatch) => {
  if (lastRestoreResult === RestoreResult.PASSED) {
    dispatch(showSnackbar(Feedback.SUCCESS, formatMessage({id: 'snackbar.restoreData.success'})));
  } else if (lastRestoreResult === RestoreResult.FAILED) {
    dispatch(showSnackbar(Feedback.ERROR, formatMessage({id: 'snackbar.restoreData.failed'})));
  }
};

export const validateBackupRestoreInProgress = () => async (_, getState) => {
  const {appState} = getState();
  const {isRestoringSoftware} = appState;
  let isInProgress = false;

  try {
    const backupRestoreStatus = await getBackupRestoreStatus();
    const {status} = backupRestoreStatus;
    const {state: backupRestoreProcess} = status;

    if (backupRestoreProcessExists(backupRestoreProcess)) {
      isInProgress = true;
    }
  } catch (error) {
    console.error(error?.message ?? error);

    if (localStorage.getItem('lastRestoreDate')) {
      isInProgress = true;
    }
  }

  if (!isInProgress && isRestoringSoftware) isInProgress = true;

  return isInProgress;
};
